import Divider from '@mui/material/Divider';
import { useCallback, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate } from 'react-router';

import { SignupType, formatAuthPath, isValidPassword, signup } from '@/auth';
import AuthLayout from '@/auth/components/AuthLayout';
import { PasswordText } from '@/auth/components/PasswordTextField';
import { SignInHandoff } from '@/auth/utils/signInHandoff';
import Button from '@/components/Button';
import { Checkbox, PhoneInput, TextField } from '@/components/FormAdapters';
import Link from '@/components/Link';
import { policies } from '@/core/agreements';
import { Tracking, getTracking } from '@/core/tracking';
import { GroupDomain } from '@/group';
import { useApp } from '@/hooks/useAppContext';
import { RootState } from '@/store';
import { isEmailValid, isPhoneValid, normalizeSpace } from '@/utils';
import { hasOnlyLetters } from '@/utils/reducer';

import JoinNetwork, { useStyles } from './JoinNetwork';
import { handleSignupError } from './handleSignupError';

interface SignupProps {
  domain?: GroupDomain;
  tags?: string[];
  signupType: SignupType;
  nextUrl?: string;
  signinLink?: string;
  signinNextUrl?: string;
  invite?: string;
  defaultEmail?: string;
  title?: string;
  shortAboutText?: string;
  longAboutText?: string;
  children?: React.ReactNode;
}

const connector = connect((state: RootState) => ({
  viewer: state.viewer,
}));

const EXPERT_PART_POLICY = policies['expert-participation-agreement'];
const TOU_POLICY = policies['terms-of-use'];
const PRIVACY_POLICY = policies.privacy;
const SMS_OPT_IN_POLICY = policies['sms-opt-in-agreement'];

const Signup = ({
  viewer,
  domain,
  tags,
  signupType,
  nextUrl,
  signinLink,
  signinNextUrl,
  invite,
  defaultEmail,
  title,
  shortAboutText,
  longAboutText,
  children,
}: SignupProps & ConnectedProps<typeof connector>) => {
  const { graphql } = useApp();
  const navigate = useNavigate();

  const isExpertSignup = signupType === 'expert';

  const s = useStyles();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const signinPath = formatAuthPath({
    to: signinLink || '/login',
    domain,
    signupType,
    invite,
    next: signinNextUrl || nextUrl,
  });

  const initialValues = useMemo(
    () => ({
      email: defaultEmail,
    }),
    [defaultEmail]
  );

  const expertAgreements = useMemo(() => {
    return isExpertSignup
      ? [
          {
            policy_code: EXPERT_PART_POLICY.apiKey,
            policy_label: EXPERT_PART_POLICY.name,
            policy_url: EXPERT_PART_POLICY.url,
          },
          ...((domain && domain.agreements) || []),
        ]
      : [];
  }, [isExpertSignup, domain]);

  const handleSubmit = useCallback(
    async (values: any) => {
      const tracking = getTracking() || ({} as Tracking);
      try {
        const recaptchaToken = executeRecaptcha ? await executeRecaptcha('signup') : '';
        const now = new Date();
        await signup(
          graphql,
          {
            recaptcha_token: recaptchaToken,
            subdomain: domain && domain.subdomain,
            email: values.email,
            phone: values.phone,
            password: values.password,
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            signup_type: signupType,
            landing_url: tracking.landing,
            invite,
            tags: tags ? (Array.isArray(tags) ? tags : [tags]) : [],
            //login: signupType === 'expert' || !domain?.group?.enforce_2fa,
            agreements: [
              {
                policy: TOU_POLICY.apiKey,
                accepted: !!values.accept_privacy_and_terms_of_use,
                updated_at: now,
              },
              {
                policy: PRIVACY_POLICY.apiKey,
                accepted: !!values.accept_privacy_and_terms_of_use,
                updated_at: now,
              },
              {
                policy: SMS_OPT_IN_POLICY.apiKey,
                accepted: !!values.accept_sms_opt_in_agreement,
                updated_at: now,
              },
              ...expertAgreements.map((a) => ({
                policy: a.policy_code,
                accepted: !!values[`accept_${a.policy_code}`],
                updated_at: now,
              })),
            ],
          },
          nextUrl || undefined
        );
      } catch (e: unknown) {
        return handleSignupError(e, (notice) => {
          const handoff: SignInHandoff = { email: values.email, notice };
          navigate(signinPath, { state: handoff });
        });
      }
    },
    [
      executeRecaptcha,
      graphql,
      domain,
      signupType,
      invite,
      tags,
      expertAgreements,
      nextUrl,
      navigate,
      signinPath,
    ]
  );

  const validate = useCallback(
    (values: any) => {
      const errors: { [key: string]: string } = {};

      if (!(values.first_name || '').trim()) {
        errors.first_name = 'Required';
      }

      if (values.first_name && !hasOnlyLetters(values.first_name)) {
        errors.first_name = 'Only letters allowed';
      }

      if (!(values.last_name || '').trim()) {
        errors.last_name = 'Required';
      }

      if (values.last_name && !hasOnlyLetters(values.last_name)) {
        errors.last_name = 'Only letters allowed';
      }

      if (!values.email) {
        errors.email = 'Required';
      } else if (!isEmailValid(values.email)) {
        errors.email = 'Invalid email address';
      }

      if (isExpertSignup && !values.phone) {
        errors.phone = 'Required';
      }

      if (values.phone && !isPhoneValid(values.phone)) {
        errors.phone = 'Phone must be valid';
      }

      if (!isValidPassword(values.password)) {
        errors.password = 'Password does not meet the requirements';
      }

      if (!values.accept_privacy_and_terms_of_use) {
        errors.accept_privacy_and_terms_of_use = 'Required';
      }

      if (isExpertSignup && !values.accept_sms_opt_in_agreement) {
        errors.accept_sms_opt_in_agreement = 'Required';
      }

      expertAgreements.forEach((a) => {
        const fieldName = `accept_${a.policy_code}`;
        if (!values[fieldName]) {
          errors[fieldName] = 'Required';
        }
      });

      return errors;
    },
    [expertAgreements, isExpertSignup]
  );

  const showJoin = viewer.id && !!domain;

  return (
    <AuthLayout
      headerChildren={children}
      domain={domain}
      title={title}
      shortAboutText={shortAboutText}
      longAboutText={longAboutText}
    >
      {showJoin ? (
        <JoinNetwork viewer={viewer} domain={domain} isExpertSignup={isExpertSignup} />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          subscription={{
            submitting: true,
            values: true,
            initialValues: true,
            touched: true,
          }}
        >
          {({ handleSubmit, submitting }: any) => {
            return (
              <form onSubmit={(e: any) => handleSubmit(e)}>
                <Field
                  component={TextField}
                  name="first_name"
                  label="First Name"
                  variant="outlined"
                  placeholder="Please provide your full first name"
                  required={!isExpertSignup}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  changeOnBlur={false}
                  inputProps={{ maxLength: 255 }}
                  format={normalizeSpace}
                />
                <Field
                  component={TextField}
                  name="last_name"
                  label="Last Name"
                  variant="outlined"
                  placeholder="Please provide your full last name"
                  required={!isExpertSignup}
                  changeOnBlur={false}
                  inputProps={{ maxLength: 255 }}
                  format={normalizeSpace}
                />
                <Field
                  component={PhoneInput}
                  name="phone"
                  label="Phone"
                  variant="outlined"
                  showExampleOnError
                />
                <Field
                  component={TextField}
                  changeOnBlur={false}
                  name="email"
                  label="Email"
                  variant="outlined"
                  inputProps={{ autoComplete: 'off' }}
                  required={!isExpertSignup}
                />
                <Field
                  component={PasswordText}
                  name="password"
                  label="Password"
                  variant="outlined"
                  showHelp
                  inputProps={{ autoComplete: 'new-password' }}
                  required={!isExpertSignup}
                />
                <div className={s.agreements}>
                  <Field
                    type="checkbox"
                    component={Checkbox}
                    classes={{ root: s.checkbox }}
                    labelClasses={{ root: s.checkboxLabel }}
                    FormControlProps={{ margin: 'none' }}
                    name="accept_privacy_and_terms_of_use"
                    label={
                      <span>
                        I have read and agree to OnFrontiers{' '}
                        <Link newTab href={PRIVACY_POLICY.url}>
                          {PRIVACY_POLICY.name}
                        </Link>{' '}
                        &amp;{' '}
                        <Link newTab href={TOU_POLICY.url}>
                          {TOU_POLICY.name}
                        </Link>
                      </span>
                    }
                  />
                  {expertAgreements.map((a) => (
                    <Field
                      key={a.policy_code}
                      type="checkbox"
                      component={Checkbox}
                      classes={{ root: s.checkbox }}
                      labelClasses={{ root: s.checkboxLabel }}
                      FormControlProps={{ margin: 'none' }}
                      name={`accept_${a.policy_code}`}
                      label={
                        <span>
                          I have read and agree with&nbsp;
                          <Link newTab href={a.policy_url}>
                            {a.policy_label}
                          </Link>
                        </span>
                      }
                    />
                  ))}
                  <Field
                    type="checkbox"
                    component={Checkbox}
                    classes={{ root: s.checkbox }}
                    labelClasses={{ root: s.checkboxLabel }}
                    FormControlProps={{ margin: 'none' }}
                    name="accept_sms_opt_in_agreement"
                    label={
                      <span>
                        {`I agree to receive text messages from OnFrontiers, including one-time verification codes and ${isExpertSignup ? 'project' : 'account'} notifications. Msg & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.`}
                      </span>
                    }
                  />
                </div>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting}
                  classes={{ root: s.button }}
                >
                  Join
                </Button>
                <Divider className={s.loginDivider} />
                <div className={s.login}>
                  Already have an account?
                  <Link to={signinPath}> Sign in</Link>
                </div>
              </form>
            );
          }}
        </Form>
      )}
    </AuthLayout>
  );
};

export default connector(Signup);
